import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileSpreadsheet } from "lucide-react";

import { AppDialog, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/utils/toast";
import { useTran } from "@/hooks";
import { useOrganizationStore } from "../hooks";
import { sanitizeOrganizationImportFile } from "../utils/excelImportSanitizer";

const LEVEL_REGEX = /^level_(\d+)$/;

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const buildLevelOptions = (maxLevel) => {
  const safeMax = Math.min(3, Math.max(1, (maxLevel || 0) + 1));
  return Array.from({ length: safeMax }, (_, index) => {
    const nextLevel = index + 1;
    return { key: `level_${nextLevel}`, level: nextLevel };
  });
};

const TEMPLATE_COMPANY_MISMATCH_MESSAGE =
  "Wrong template for selected company. Please re-upload the correct file or re-download the template and fill it again.";
const TEMPLATE_METADATA_MISSING_MESSAGE =
  "Template metadata is missing. Please re-download the template and fill it again.";

const getScopeValidationMessage = ({
  hasFile,
  meta,
  requireCompany,
  currentCompanyId,
  t,
}) => {
  if (!hasFile || !meta || !requireCompany) return "";

  const metaCompanyId = String(meta?.companyId || "");
  if (!metaCompanyId) {
    return t(TEMPLATE_METADATA_MISSING_MESSAGE);
  }

  if (
    currentCompanyId &&
    String(currentCompanyId).trim() &&
    String(currentCompanyId) !== metaCompanyId
  ) {
    return t(TEMPLATE_COMPANY_MISMATCH_MESSAGE);
  }

  return "";
};

export default function OrganizationExcelDialog({
  open,
  onOpenChange,
  downloading = false,
  importing = false,
  requireCompany = false,
  companyOptions = [],
  getCompanyId,
  onDownloadTemplate,
  onImport,
}) {
  const { t } = useTran();
  const { items, maxLevel } = useOrganizationStore();
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState("level_1");
  const [checkingRows, setCheckingRows] = useState(false);
  const [hasFilledRows, setHasFilledRows] = useState(false);
  const [fileValidationMsg, setFileValidationMsg] = useState("");
  const [sanitizedPayload, setSanitizedPayload] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const validationSeqRef = useRef(0);
  const fileInputRef = useRef(null);
  const resetSelectedFileState = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFile(null);
    setSanitizedPayload(null);
    setHasFilledRows(false);
    setCheckingRows(false);
    setFileValidationMsg("");
  }, []);
  const initialCompanyId = useMemo(() => {
    const id = getCompanyId?.();
    if (!id || id === "All") return "";
    return String(id);
  }, [getCompanyId]);

  useEffect(() => {
    if (open) {
      setCompanyId(initialCompanyId);
      return;
    }

    validationSeqRef.current += 1;
    resetSelectedFileState();
    setLevel("level_1");
    setCompanyId(initialCompanyId);
  }, [open, initialCompanyId, resetSelectedFileState]);

  const resolvedCompanyId = useMemo(() => {
    const rawId = requireCompany ? companyId : getCompanyId?.();
    if (!rawId || rawId === "All") return undefined;
    return String(rawId);
  }, [companyId, getCompanyId, requireCompany]);

  const normalizedCompanyOptions = useMemo(
    () =>
      (companyOptions || []).map((option) => ({
        ...option,
        value: String(option?.value ?? option?.key ?? ""),
      })),
    [companyOptions],
  );
  const templateMeta = sanitizedPayload?.templateMeta || null;
  const scopeValidationMsg = useMemo(() => {
    return getScopeValidationMessage({
      hasFile: Boolean(file),
      meta: templateMeta,
      requireCompany,
      currentCompanyId: resolvedCompanyId,
      t,
    });
  }, [file, templateMeta, requireCompany, resolvedCompanyId, t]);

  const maxLevelForSelectedCompany = useMemo(() => {
    if (!requireCompany) return maxLevel;
    if (!resolvedCompanyId) return 0;

    return (items || []).reduce((mx, unit) => {
      const unitCompanyId = String(unit?.companyId ?? "");
      if (unitCompanyId !== String(resolvedCompanyId)) return mx;
      const unitLevel = getLevelNumber(unit?.unitLevel);
      return unitLevel && unitLevel > mx ? unitLevel : mx;
    }, 0);
  }, [items, maxLevel, requireCompany, resolvedCompanyId]);

  const levelOptions = useMemo(
    () =>
      buildLevelOptions(maxLevelForSelectedCompany).map((option) => ({
        key: option.key,
        label: `${t("Level")} ${option.level}`,
        level: option.level,
      })),
    [t, maxLevelForSelectedCompany],
  );

  useEffect(() => {
    if (!open) return;
    if (!levelOptions.length) return;
    const allowed = levelOptions.some((option) => option.key === level);
    if (allowed) return;
    setLevel(levelOptions[0].key);
  }, [open, level, levelOptions]);

  const ensureCompanyOk = () => {
    if (!requireCompany) return true;
    if (!resolvedCompanyId) {
      showToast.error(t("Please select a company first."));
      return false;
    }
    return true;
  };

  const handleCompanyChange = (value) => {
    setCompanyId(String(value || ""));
    setLevel("level_1");
    validationSeqRef.current += 1;
    resetSelectedFileState();
  };

  const handleDownload = async () => {
    if (!ensureCompanyOk()) return;
    try {
      await onDownloadTemplate?.({
        companyId: resolvedCompanyId,
        level,
      });
      showToast.success(t("Template downloaded."));
    } catch (e) {
      showToast.error(e?.message || t("Failed to download template."));
    }
  };

  const handleImport = async () => {
    if (!file) {
      showToast.error(t("Please choose an Excel file."));
      return;
    }
    if (!hasFilledRows) {
      showToast.error(
        t("No filled rows found in Excel. Please fill at least one row."),
      );
      return;
    }
    if (scopeValidationMsg) {
      showToast.error(scopeValidationMsg);
      return;
    }
    if (!ensureCompanyOk()) return;

    try {
      const result = await onImport?.(sanitizedPayload || file, {
        companyId: resolvedCompanyId,
      });
      const failed = Number(result?.failed || 0);
      const created = Number(result?.created || 0);

      if (failed > 0) {
        const firstMsg = result?.errors?.[0]?.message || t("Some rows failed.");
        showToast.error(
          `${t("Import failed")} (${failed} ${t("rows")}): ${firstMsg}`,
        );
        return;
      }

      onOpenChange?.(false);
      resetSelectedFileState();
      showToast.success(
        created > 0
          ? `${t("Imported successfully.")} (${created} ${t("rows")})`
          : t("Imported successfully."),
      );
    } catch (e) {
      showToast.error(e?.message || t("Import failed."));
    }
  };

  const handleFileChange = async (event) => {
    if (requireCompany && !resolvedCompanyId) {
      showToast.error(t("Please select a company first."));
      return;
    }

    const selected = event.target.files?.[0] || null;
    const validationId = ++validationSeqRef.current;
    setFile(selected);
    setSanitizedPayload(null);
    setHasFilledRows(false);
    setFileValidationMsg("");

    if (!selected) return;

    setCheckingRows(true);
    try {
      const prepared = await sanitizeOrganizationImportFile(selected);
      if (validationId !== validationSeqRef.current) return;
      const valid = prepared.dataRows > 0;
      setSanitizedPayload(prepared);
      setHasFilledRows(valid);
      const mismatchMsg = getScopeValidationMessage({
        hasFile: Boolean(selected),
        meta: prepared.templateMeta,
        requireCompany,
        currentCompanyId: resolvedCompanyId,
        t,
      });
      if (mismatchMsg) {
        showToast.error(mismatchMsg);
      }
      if (!valid) {
        setFileValidationMsg(
          t("No filled rows found in Excel. Please fill at least one row."),
        );
      }
    } catch {
      if (validationId !== validationSeqRef.current) return;
      setHasFilledRows(false);
      setSanitizedPayload(null);
      setFileValidationMsg(
        t("Unable to read this file. Please choose a valid Excel file."),
      );
    } finally {
      if (validationId === validationSeqRef.current) {
        setCheckingRows(false);
      }
    }
  };

  const isImportDisabled =
    importing ||
    checkingRows ||
    !file ||
    !hasFilledRows ||
    Boolean(scopeValidationMsg);
  const importDisabledWithCompany =
    isImportDisabled || (requireCompany && !resolvedCompanyId);
  const disableLevelUntilCompany = requireCompany && !resolvedCompanyId;
  const disableChooseFileUntilCompany = requireCompany && !resolvedCompanyId;

  const selectedFileName = file?.name || "";
  const combinedValidationMsg = scopeValidationMsg || fileValidationMsg;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("Import Organization Units")}
      description={t(
        "Download template, fill it, then upload Excel to import in batch.",
      )}
      icon={FileSpreadsheet}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              validationSeqRef.current += 1;
              onOpenChange?.(false);
              resetSelectedFileState();
              setCompanyId(initialCompanyId);
              setLevel("level_1");
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={importDisabledWithCompany}
          >
            {importing ? t("Importing...") : t("Import")}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {requireCompany ? (
          <SelectField
            options={normalizedCompanyOptions}
            value={companyId || undefined}
            onValueChange={handleCompanyChange}
            label={t("Parent Company")}
            placeholder={t("Select company")}
            emptyLabel={t("No companies available")}
          />
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <SelectField
              options={levelOptions}
              value={level}
              onValueChange={setLevel}
              label={t("Unit Level")}
              placeholder={t("Select unit level")}
              className="flex-1"
              selectClassName="min-w-[200px]"
              disabled={disableLevelUntilCompany}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleDownload}
            disabled={downloading || (requireCompany && !resolvedCompanyId)}
            className="shrink-0 sm:w-auto"
          >
            {downloading ? t("Downloading...") : t("Download Template")}
          </Button>
        </div>
        {disableLevelUntilCompany ? (
          <p className="text-xs text-muted-foreground">
            {t("Select a company first")}
          </p>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={disableChooseFileUntilCompany}
          className="hidden"
        />
        <div className="rounded-md border border-dashed bg-muted/30 p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {selectedFileName || t("No file selected")}
              </p>
              <p className="text-xs text-muted-foreground">
                {checkingRows
                  ? t("Checking file...")
                  : t("Accepted format: .xlsx, .xls")}
              </p>
            </div>
            <Button
              type="button"
              onClick={() => {
                if (fileInputRef.current) {
                  // Allow re-selecting the same file and still trigger onChange.
                  fileInputRef.current.value = "";
                  fileInputRef.current.click();
                }
              }}
              disabled={importing || disableChooseFileUntilCompany}
            >
              {selectedFileName ? t("Change File") : t("Choose File")}
            </Button>
          </div>
          {disableChooseFileUntilCompany ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t("Select a company to enable file upload.")}
            </p>
          ) : null}
        </div>
        {combinedValidationMsg ? (
          <div className="text-xs text-destructive">
            {combinedValidationMsg}
          </div>
        ) : null}
      </div>
    </AppDialog>
  );
}
